import { useTheme } from '../theme'
import { api, fixImageUrl, formatDate } from '../api'
import { useCachedFetch } from '../lib/swr'
import { modales } from '../contexts/modales'

/**
 * Fiche publique d'un producteur — fidele au prototype v7 (ecran mProducteur) :
 * carte sombre (photo ronde, nom serif, commune, badges certifications validees),
 * « Le producteur » (presentation), « Sa production » (grille 2 colonnes).
 *
 * Props :
 * - `eventAfficherPrix` (boolean) : si false, masque tous les prix (intention
 *   metier conservee : le contexte evenement decide de l'affichage des prix).
 * - `emplacement` (string) : numero de stand DANS le contexte d'un evenement
 *   (donnee tierce). Jamais affiche hors contexte — le producteur connecte ne
 *   voit pas d'emplacement sur son propre profil (v7.1 sujet 4).
 */
function FicheProducteur({ producteurId, onClose, onExploreMarche, eventAfficherPrix, emplacement }) {
  const { isWeb3 } = useTheme()
  // Cache SWR (S29, E-13) : rouvrir une fiche deja consultee est instantane,
  // la revalidation se fait en silence. Avant, chaque ouverture repassait par
  // le squelette de chargement.
  const { data, loading } = useCachedFetch(
    producteurId ? `producteur/${producteurId}` : null,
    () => api.getProducteur(producteurId),
    { enabled: !!producteurId },
  )

  const t = {
    bg: isWeb3 ? 'bg-[#0a0a0f]' : 'bg-creme',
    cardBg: 'bg-card border-card-border',
    textPrimary: 'text-ink',
    textSecondary: 'text-ink/60',
    textMuted: 'text-ink/60',
    closeBg: isWeb3 ? 'bg-white/10 text-white' : 'bg-white/80 text-green',
    itemBg: isWeb3 ? 'bg-white/5 border-white/10' : 'bg-white/80 border-green/8',
  }

  const nom = data?.nomCommercial || data?.nom
  const certifications = data?.certifications || []

  return (
    <div className={`fixed inset-0 z-[100] ${t.bg} overflow-y-auto`}>
      {/* Header fixe */}
      <div className={`sticky top-0 z-10 flex items-center gap-3 px-4 py-3 backdrop-blur-xl border-b ${
        isWeb3 ? 'bg-[#0a0a0f]/90 border-white/10' : 'bg-creme/90 border-green/10'
      }`}>
        <button
          onClick={onClose}
          aria-label="Retour"
          className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${t.closeBg}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className={`text-sm font-semibold ${t.textPrimary}`}>Fiche producteur</span>
      </div>

      {loading ? (
        <div className="p-6 space-y-4 animate-pulse">
          <div className={`h-40 rounded-[28px] bg-ink/5`} />
          <div className={`h-40 rounded-2xl bg-ink/5`} />
        </div>
      ) : !data ? (
        <div className="text-center pt-20">
          <p className="text-3xl mb-3">😕</p>
          <p className={`text-sm ${t.textSecondary}`}>Producteur introuvable</p>
          <button onClick={onClose} className="mt-4 px-5 py-2 rounded-xl text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}>
            Retour
          </button>
        </div>
      ) : (
        <div className="px-4 py-5 pb-20">
          {/* ===== Carte identite (glassDark, fidele proto) ===== */}
          <div className="glass-dark rounded-[28px] p-6 relative overflow-hidden mb-4" style={{ color: 'rgb(var(--c-creme))' }}>
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgb(var(--c-teal) / 0.4), transparent 70%)' }} />
            <div className="relative flex items-center gap-3.5">
              <span
                className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center text-3xl flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,rgb(var(--c-teal)),#0d6b5f)', border: '2px solid rgb(var(--c-creme) / 0.5)' }}
              >
                {data.logo
                  ? <img src={fixImageUrl(data.logo)} alt={nom || 'Producteur'} className="w-full h-full object-cover" />
                  : '🧑🏾‍🌾'}
              </span>
              <div className="min-w-0">
                <p className="font-serif text-[24px] leading-tight">{nom}</p>
                {(data.ville || emplacement) && (
                  <p className="text-[12.5px] mt-0.5" style={{ color: 'rgb(var(--c-creme) / 0.75)' }}>
                    📍 {data.ville || ''}{emplacement ? `${data.ville ? ' · ' : ''}stand ${emplacement}` : ''}
                  </p>
                )}
              </div>
            </div>
            {certifications.length > 0 && (
              <div className="relative flex gap-1.5 mt-4 flex-wrap">
                {certifications.map((c) => (
                  <span key={c.code}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full"
                    style={{ color: 'rgb(var(--c-creme))', background: 'rgb(var(--c-creme) / 0.14)', border: '1px solid rgb(var(--c-creme) / 0.3)' }}>
                    {c.emoji} {c.nom}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ===== Le producteur (presentation) ===== */}
          {data.description && (
            <>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 px-0.5" style={{ color: 'rgb(var(--c-teal))' }}>
                Le producteur
              </p>
              <div className={`rounded-[20px] border p-4 mb-4 ${t.cardBg}`}>
                <p className={`text-[13.5px] leading-relaxed ${isWeb3 ? 'text-white/80' : 'text-ink/80'}`}>{data.description}</p>
              </div>
            </>
          )}

          {/* ===== Sa production (grille 2 colonnes, fidele proto) ===== */}
          {data.offers && data.offers.length > 0 && (
            <>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2.5 px-0.5" style={{ color: 'rgb(var(--c-primary))' }}>
                Sa production ({data.offers.length})
              </p>
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                {data.offers.map(offer => (
                  <div key={offer.id} className={`rounded-[18px] border p-3 flex items-center gap-2.5 ${t.itemBg}`}>
                    <span className={`w-10 h-10 rounded-lg ${isWeb3 ? 'bg-black/30' : 'bg-ink/[0.06]'} flex items-center justify-center flex-shrink-0 overflow-hidden text-[22px]`}>
                      {offer.product?.photos?.length > 0
                        ? <img src={fixImageUrl(offer.product.photos[0])} alt={offer.product?.nom} className="w-full h-full object-cover rounded-lg" />
                        : (offer.product?.category?.emoji || '🥭')}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-[13px] font-bold truncate ${t.textPrimary}`}>{offer.product?.nom || 'Produit'}</p>
                      {eventAfficherPrix !== false && offer.prix != null ? (
                        <p className="text-[11px] font-bold" style={{ color: 'rgb(var(--c-primary))' }}>
                          {Number(offer.prix).toFixed(2).replace('.', ',')}€{offer.prixUnit ? `/${offer.prixUnit}` : ''}
                        </p>
                      ) : (
                        <p className={`text-[11px] ${t.textMuted}`}>Sur place</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ===== Prochains marches ===== */}
          {data['prochainsMarchés'] && data['prochainsMarchés'].length > 0 && (
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-2 px-0.5 ${t.textMuted}`}>
                Prochains marchés
              </p>
              <div className="space-y-2">
                {data['prochainsMarchés'].map(m => (
                  <button
                    key={m.id}
                    onClick={() => onExploreMarche && onExploreMarche(m)}
                    className={`w-full text-left flex items-center gap-3 p-3 border rounded-xl transition-all hover:border-primary/40 ${t.itemBg}`}
                  >
                    <span className="text-xl">{m.typeConfig?.icone || '🎪'}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${t.textPrimary}`}>{m.lieu || 'Évènement'}</p>
                      <p className={`text-xs ${t.textSecondary}`}>{formatDate(m.date)}</p>
                    </div>
                    <span className={`text-[10px] ${t.textMuted}`}>Voir →</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FicheProducteur

/**
 * Ouvreur imperatif (ADR-066). `surExploreMarche(marcheId)` : navigation vers
 * le module Marche — la fermeture est explicite ici, et le changement de
 * route fermerait de toute facon la pile (regle du ModalesProvider).
 * `eventAfficherPrix` respecte le toggle « Afficher les prix » du BO herite
 * du contexte d'ouverture (Explorer / module Marche).
 */
export function ouvrirFicheProducteur({ id, eventAfficherPrix, emplacement }, { surExploreMarche } = {}) {
  if (!id) return
  modales.ouvrir({
    id: `fiche-producteur-${id}`,
    contenu: ({ fermer }) => (
      <FicheProducteur
        producteurId={id}
        eventAfficherPrix={eventAfficherPrix}
        emplacement={emplacement}
        onClose={fermer}
        onExploreMarche={(marche) => { fermer(); surExploreMarche?.(marche?.id) }}
      />
    ),
  })
}
